import React, { useState } from "react";
import { useTourStore } from "../store/TourStore";
import { useAuthStore } from "../store/AuthStore";
import { useBirdRadarStore } from "../store/RadarStore";
import { useMap } from "@vis.gl/react-google-maps";

// ── Tour Planner Localization ────────────────────────────────────────────────
const TOUR_UI_TEXT: Record<string, Record<string, string>> = {
    en: {
        title: "Tour Planner",
        tabSearch: "🔍 Search",
        tabQuests: "💾 Saved Quests",
        searchPlaceholder: "Bird, Family or Scientific Name...",
        radius25: "25km Radius",
        radius50: "50km Radius",
        radius100: "100km Radius",
        radius250: "250km Radius",
        radius500: "500km (National)",
        days7: "Last 7 Days",
        days14: "Last 14 Days",
        days30: "Last 30 Days",
        days365: "Last 365 Days",
        scanning: "Scanning World...",
        findHotspots: "Find Hotspots",
        saveQuestPlaceholder: "Name this quest (e.g. Eagle Quest)",
        save: "Save",
        signInToSave: "Sign in to save this search as a Quest",
        scanningHotspots: "Scanning hotspots...",
        noHotspotsTitle: "No hotspots found",
        noHotspotsHint: "Try a larger radius or more days",
        emptySearchTitle: "Find your target bird",
        emptySearchHint: "Type any name above and hit Find Hotspots",
        seen: "seen",
        flyToMap: "→ Fly to map",
        highlightedOnMap: "Highlighted on map",
        signInQuestsTitle: "Sign in to see your Quests",
        signInQuestsHint: "Saved searches are tied to your account",
        noQuestsTitle: "No saved quests yet",
        noQuestsHint: "Run a search, then save it with a label",
        goToSearch: "Go to Search →",
        savedQuestsCount_one: "saved quest",
        savedQuestsCount_other: "saved quests",
        unknownTarget: "Unknown target",
        runQuest: "Run this quest",
        delete: "Delete",
    },
    es: {
        title: "Planificador de Tours",
        tabSearch: "🔍 Buscar",
        tabQuests: "💾 Quests Guardados",
        searchPlaceholder: "Ave, Familia o Nombre Científico...",
        radius25: "Radio 25km",
        radius50: "Radio 50km",
        radius100: "Radio 100km",
        radius250: "Radio 250km",
        radius500: "500km (Nacional)",
        days7: "Últimos 7 días",
        days14: "Últimos 14 días",
        days30: "Últimos 30 días",
        days365: "Últimos 365 días",
        scanning: "Escaneando el mundo...",
        findHotspots: "Encontrar Puntos Calientes",
        saveQuestPlaceholder: "Nombra este quest (ej. Quest de Águilas)",
        save: "Guardar",
        signInToSave: "Inicia sesión para guardar esta búsqueda como Quest",
        scanningHotspots: "Escaneando puntos calientes...",
        noHotspotsTitle: "No se encontraron puntos calientes",
        noHotspotsHint: "Intenta con un radio mayor o más días",
        emptySearchTitle: "Encuentra tu ave objetivo",
        emptySearchHint: "Escribe un nombre arriba y presiona Encontrar Puntos Calientes",
        seen: "vistos",
        flyToMap: "→ Ir al mapa",
        highlightedOnMap: "Resaltado en el mapa",
        signInQuestsTitle: "Inicia sesión para ver tus Quests",
        signInQuestsHint: "Las búsquedas guardadas están asociadas a tu cuenta",
        noQuestsTitle: "Aún no hay quests guardados",
        noQuestsHint: "Realiza una búsqueda y guárdala con un nombre",
        goToSearch: "Ir a Buscar →",
        savedQuestsCount_one: "quest guardado",
        savedQuestsCount_other: "quests guardados",
        unknownTarget: "Objetivo desconocido",
        runQuest: "Ejecutar este quest",
        delete: "Eliminar",
    },
    fr: {
        title: "Planificateur de Tours",
        tabSearch: "🔍 Rechercher",
        tabQuests: "💾 Quêtes Sauvegardées",
        searchPlaceholder: "Oiseau, Famille ou Nom Scientifique...",
        radius25: "Rayon 25km",
        radius50: "Rayon 50km",
        radius100: "Rayon 100km",
        radius250: "Rayon 250km",
        radius500: "500km (National)",
        days7: "7 derniers jours",
        days14: "14 derniers jours",
        days30: "30 derniers jours",
        days365: "365 derniers jours",
        scanning: "Exploration mondiale...",
        findHotspots: "Trouver des Points Chauds",
        saveQuestPlaceholder: "Nommer cette quête (ex. Quête des Aigles)",
        save: "Sauvegarder",
        signInToSave: "Connectez-vous pour sauvegarder cette recherche comme Quête",
        scanningHotspots: "Recherche de points chauds...",
        noHotspotsTitle: "Aucun point chaud trouvé",
        noHotspotsHint: "Essayez un rayon plus grand ou plus de jours",
        emptySearchTitle: "Trouvez votre oiseau cible",
        emptySearchHint: "Tapez un nom ci-dessus et appuyez sur Trouver des Points Chauds",
        seen: "observés",
        flyToMap: "→ Aller à la carte",
        highlightedOnMap: "Mis en évidence sur la carte",
        signInQuestsTitle: "Connectez-vous pour voir vos Quêtes",
        signInQuestsHint: "Les recherches sauvegardées sont liées à votre compte",
        noQuestsTitle: "Aucune quête sauvegardée",
        noQuestsHint: "Effectuez une recherche puis sauvegardez-la avec un nom",
        goToSearch: "Aller à Rechercher →",
        savedQuestsCount_one: "quête sauvegardée",
        savedQuestsCount_other: "quêtes sauvegardées",
        unknownTarget: "Cible inconnue",
        runQuest: "Lancer cette quête",
        delete: "Supprimer",
    },
    pt: {
        title: "Planejador de Tours",
        tabSearch: "🔍 Pesquisar",
        tabQuests: "💾 Quests Salvos",
        searchPlaceholder: "Ave, Família ou Nome Científico...",
        radius25: "Raio 25km",
        radius50: "Raio 50km",
        radius100: "Raio 100km",
        radius250: "Raio 250km",
        radius500: "500km (Nacional)",
        days7: "Últimos 7 dias",
        days14: "Últimos 14 dias",
        days30: "Últimos 30 dias",
        days365: "Últimos 365 dias",
        scanning: "Varrendo o mundo...",
        findHotspots: "Encontrar Pontos Quentes",
        saveQuestPlaceholder: "Nomear este quest (ex. Quest das Águias)",
        save: "Salvar",
        signInToSave: "Entre para salvar esta pesquisa como Quest",
        scanningHotspots: "Varrendo pontos quentes...",
        noHotspotsTitle: "Nenhum ponto quente encontrado",
        noHotspotsHint: "Tente um raio maior ou mais dias",
        emptySearchTitle: "Encontre sua ave alvo",
        emptySearchHint: "Digite um nome acima e clique em Encontrar Pontos Quentes",
        seen: "vistos",
        flyToMap: "→ Ir ao mapa",
        highlightedOnMap: "Destacado no mapa",
        signInQuestsTitle: "Entre para ver seus Quests",
        signInQuestsHint: "As pesquisas salvas estão ligadas à sua conta",
        noQuestsTitle: "Nenhum quest salvo ainda",
        noQuestsHint: "Faça uma pesquisa e salve-a com um nome",
        goToSearch: "Ir para Pesquisar →",
        savedQuestsCount_one: "quest salvo",
        savedQuestsCount_other: "quests salvos",
        unknownTarget: "Alvo desconhecido",
        runQuest: "Executar este quest",
        delete: "Excluir",
    },
    de: {
        title: "Tour-Planer",
        tabSearch: "🔍 Suchen",
        tabQuests: "💾 Gespeicherte Quests",
        searchPlaceholder: "Vogel, Familie oder Wissenschaftlicher Name...",
        radius25: "Radius 25km",
        radius50: "Radius 50km",
        radius100: "Radius 100km",
        radius250: "Radius 250km",
        radius500: "500km (National)",
        days7: "Letzte 7 Tage",
        days14: "Letzte 14 Tage",
        days30: "Letzte 30 Tage",
        days365: "Letzte 365 Tage",
        scanning: "Weltweite Suche...",
        findHotspots: "Hotspots Finden",
        saveQuestPlaceholder: "Quest benennen (z.B. Adler-Quest)",
        save: "Speichern",
        signInToSave: "Anmelden, um diese Suche als Quest zu speichern",
        scanningHotspots: "Hotspots werden gesucht...",
        noHotspotsTitle: "Keine Hotspots gefunden",
        noHotspotsHint: "Versuchen Sie einen größeren Radius oder mehr Tage",
        emptySearchTitle: "Finden Sie Ihren Ziel-Vogel",
        emptySearchHint: "Tippen Sie einen Namen ein und klicken Sie auf Hotspots Finden",
        seen: "gesichtet",
        flyToMap: "→ Zur Karte",
        highlightedOnMap: "Auf der Karte hervorgehoben",
        signInQuestsTitle: "Anmelden, um Ihre Quests zu sehen",
        signInQuestsHint: "Gespeicherte Suchen sind mit Ihrem Konto verknüpft",
        noQuestsTitle: "Noch keine gespeicherten Quests",
        noQuestsHint: "Durchsuchen und mit einem Namen speichern",
        goToSearch: "Zur Suche →",
        savedQuestsCount_one: "gespeicherter Quest",
        savedQuestsCount_other: "gespeicherte Quests",
        unknownTarget: "Unbekanntes Ziel",
        runQuest: "Diesen Quest ausführen",
        delete: "Löschen",
    },
    zh: {
        title: "旅行规划器",
        tabSearch: "🔍 搜索",
        tabQuests: "💾 已保存的任务",
        searchPlaceholder: "鸟类、科或学名...",
        radius25: "半径 25公里",
        radius50: "半径 50公里",
        radius100: "半径 100公里",
        radius250: "半径 250公里",
        radius500: "500公里（全国）",
        days7: "最近7天",
        days14: "最近14天",
        days30: "最近30天",
        days365: "最近365天",
        scanning: "全球扫描中...",
        findHotspots: "查找热点",
        saveQuestPlaceholder: "命名此任务（例：寻鹰任务）",
        save: "保存",
        signInToSave: "登录以将此搜索保存为任务",
        scanningHotspots: "正在扫描热点...",
        noHotspotsTitle: "未找到热点",
        noHotspotsHint: "尝试更大的半径或更多天数",
        emptySearchTitle: "查找您的目标鸟类",
        emptySearchHint: "在上方输入名称，然后点击查找热点",
        seen: "次记录",
        flyToMap: "→ 飞至地图",
        highlightedOnMap: "已在地图上高亮显示",
        signInQuestsTitle: "登录以查看您的任务",
        signInQuestsHint: "已保存的搜索与您的账户关联",
        noQuestsTitle: "暂无已保存的任务",
        noQuestsHint: "进行搜索，然后用名称保存",
        goToSearch: "前往搜索 →",
        savedQuestsCount_one: "个已保存任务",
        savedQuestsCount_other: "个已保存任务",
        unknownTarget: "未知目标",
        runQuest: "执行此任务",
        delete: "删除",
    },
};

type Tab = "search" | "quests";

export default function TourPlannerPanel({ 
    currentLat, 
    currentLng 
}: { 
    currentLat: number | null, 
    currentLng: number | null 
}) {
    const { user, idToken } = useAuthStore();
    const { language } = useBirdRadarStore();
    const map = useMap();
    const { 
        tourRadiusKm, 
        tourDaysAgo, 
        tourSearchTerm, 
        setTourParams,
        tourLocations,
        isTourLoading,
        fetchTourLocations,
        saveTarget,
        savedTargets,
        fetchSavedTargets,
        deleteTarget,
        selectedTourLocationId,
        setSelectedTourLocationId
    } = useTourStore();

    const t = TOUR_UI_TEXT[language] || TOUR_UI_TEXT["en"];
    const questCount = savedTargets.length;
    const savedQuestsLabel = `${questCount} ${questCount === 1 ? t.savedQuestsCount_one : t.savedQuestsCount_other}`;

    const [activeTab, setActiveTab] = useState<Tab>("search");
    const [searchInput, setSearchInput] = useState(tourSearchTerm || "");
    const [saveLabel, setSaveLabel] = useState("");

    React.useEffect(() => {
        if (idToken) {
            fetchSavedTargets(idToken);
        }
    }, [idToken, fetchSavedTargets]);

    const handleSearch = () => {
        if (!currentLat || !currentLng) return;
        setTourParams(tourRadiusKm, tourDaysAgo, searchInput.trim());
        setTimeout(() => fetchTourLocations(currentLat, currentLng), 100);
    };

    const handleMapFly = (lat: number, lng: number, locationId: string) => {
        setSelectedTourLocationId(locationId);
        if (map) {
            map.panTo({ lat, lng });
            map.setZoom(14);
        }
    };

    const handleSave = () => {
        if (idToken && saveLabel) {
            saveTarget(idToken, saveLabel);
            setSaveLabel("");
        }
    };

    const handleLoadTarget = (target: any) => {
        const term = target.searchTerm || target.speciesName || "";
        setSearchInput(term);
        setTourParams(tourRadiusKm, tourDaysAgo, term);
        setActiveTab("search");
        if (currentLat && currentLng) {
            setTimeout(() => fetchTourLocations(currentLat, currentLng), 100);
        }
    };

    return (
        <div className="flex flex-col h-full">

            {/* Header */}
            <div className="sticky top-0 z-10 bg-neutral-950/95 backdrop-blur-sm border-b border-neutral-800 px-4 pt-4 pb-0">
                <h2 className="text-base font-bold bg-gradient-to-r from-emerald-400 to-indigo-400 bg-clip-text text-transparent mb-3">
                    🏆 {t.title}
                </h2>

                {/* Tab switcher */}
                <div className="flex gap-0 border-b border-neutral-800 -mb-px">
                    <button
                        onClick={() => setActiveTab("search")}
                        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                            activeTab === "search"
                                ? "border-indigo-500 text-indigo-400"
                                : "border-transparent text-neutral-500 hover:text-neutral-300"
                        }`}
                    >
                        {t.tabSearch}
                    </button>
                    <button
                        onClick={() => setActiveTab("quests")}
                        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
                            activeTab === "quests"
                                ? "border-emerald-500 text-emerald-400"
                                : "border-transparent text-neutral-500 hover:text-neutral-300"
                        }`}
                    >
                        {t.tabQuests}
                        {savedTargets.length > 0 && (
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                                activeTab === "quests" ? "bg-emerald-500/20 text-emerald-400" : "bg-neutral-800 text-neutral-500"
                            }`}>
                                {savedTargets.length}
                            </span>
                        )}
                    </button>
                </div>
            </div>

            {/* Scrollable content area */}
            <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4">

                {/* ─── SEARCH TAB ─── */}
                {activeTab === "search" && (
                    <>
                        {/* Search Form */}
                        <div className="bg-neutral-900 border border-neutral-700 rounded-xl p-4 flex flex-col gap-3">
                            <div className="relative">
                                <input 
                                    type="text" 
                                    placeholder={t.searchPlaceholder}
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                                    className="w-full bg-black/50 border border-neutral-800 text-sm text-white px-3 py-2.5 rounded-lg focus:border-indigo-500 outline-none pr-10"
                                />
                                {searchInput && (
                                    <button 
                                        onClick={() => { setSearchInput(""); setTourParams(tourRadiusKm, tourDaysAgo, ""); }}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white text-xs"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>

                            <div className="flex gap-2">
                                <select 
                                    value={tourRadiusKm} 
                                    onChange={(e) => setTourParams(Number(e.target.value), tourDaysAgo, tourSearchTerm)}
                                    className="bg-black/50 border border-neutral-800 text-sm text-white px-2 py-2 rounded-lg flex-1"
                                >
                                    <option value={25}>{t.radius25}</option>
                                    <option value={50}>{t.radius50}</option>
                                    <option value={100}>{t.radius100}</option>
                                    <option value={250}>{t.radius250}</option>
                                    <option value={500}>{t.radius500}</option>
                                </select>

                                <select 
                                    value={tourDaysAgo} 
                                    onChange={(e) => setTourParams(tourRadiusKm, Number(e.target.value), tourSearchTerm)}
                                    className="bg-black/50 border border-neutral-800 text-sm text-white px-2 py-2 rounded-lg flex-1"
                                >
                                    <option value={7}>{t.days7}</option>
                                    <option value={14}>{t.days14}</option>
                                    <option value={30}>{t.days30}</option>
                                    <option value={365}>{t.days365}</option>
                                </select>
                            </div>

                            <button 
                                onClick={handleSearch}
                                disabled={isTourLoading || !searchInput.trim()}
                                className="bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:bg-neutral-800 text-white font-semibold py-2.5 rounded-lg transition shadow-lg shadow-indigo-500/20"
                            >
                                {isTourLoading ? t.scanning : t.findHotspots}
                            </button>
                        </div>

                        {/* Save Quest bar — appears after a search */}
                        {user && tourSearchTerm && (
                            <div className="flex gap-2 items-center bg-indigo-900/20 p-3 rounded-xl border border-indigo-500/20">
                                <span className="text-lg">🏆</span>
                                <input 
                                    type="text" 
                                    placeholder={t.saveQuestPlaceholder}
                                    value={saveLabel}
                                    onChange={(e) => setSaveLabel(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                                    className="bg-black/50 border border-indigo-500/50 text-xs text-white px-2 py-1.5 rounded flex-1"
                                />
                                <button 
                                    onClick={handleSave}
                                    disabled={!saveLabel.trim()}
                                    className="bg-indigo-500 hover:bg-indigo-400 disabled:opacity-50 text-white text-xs px-3 py-1.5 rounded font-medium transition"
                                >
                                    {t.save}
                                </button>
                            </div>
                        )}

                        {/* Not logged in hint */}
                        {!user && tourSearchTerm && (
                            <p className="text-neutral-600 text-xs text-center">
                                {t.signInToSave}
                            </p>
                        )}

                        {/* Results */}
                        <div className="flex flex-col gap-3">
                            {isTourLoading && (
                                <div className="flex flex-col items-center py-10 gap-3">
                                    <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                                    <p className="text-neutral-400 text-sm">{t.scanningHotspots}</p>
                                </div>
                            )}

                            {!isTourLoading && tourLocations.length === 0 && tourSearchTerm && (
                                <div className="text-center p-8 bg-neutral-900/50 rounded-xl border border-neutral-800">
                                    <div className="text-4xl mb-2">🌿</div>
                                    <p className="text-neutral-400 text-sm font-medium">{t.noHotspotsTitle}</p>
                                    <p className="text-neutral-600 text-xs mt-1">{t.noHotspotsHint}</p>
                                </div>
                            )}

                            {!isTourLoading && !tourSearchTerm && (
                                <div className="text-center p-8 bg-neutral-900/30 rounded-xl border border-dashed border-neutral-800">
                                    <div className="text-4xl mb-2">🔭</div>
                                    <p className="text-neutral-400 text-sm font-medium">{t.emptySearchTitle}</p>
                                    <p className="text-neutral-600 text-xs mt-1">{t.emptySearchHint}</p>
                                </div>
                            )}

                            {!isTourLoading && tourLocations.map(loc => {
                                const isSelected = selectedTourLocationId === loc.locationId;
                                return (
                                <div 
                                    key={loc.locationId} 
                                    onClick={() => handleMapFly(loc.latitude, loc.longitude, loc.locationId)}
                                    className={`cursor-pointer rounded-xl transition overflow-hidden group border ${
                                        isSelected
                                            ? "bg-indigo-950/60 border-indigo-500 shadow-lg shadow-indigo-500/20"
                                            : "bg-neutral-900 hover:bg-neutral-800 border-neutral-800"
                                    }`}
                                >
                                    {/* Header row */}
                                    <div className="p-3 flex justify-between items-start">
                                        <div className="flex-1 min-w-0 mr-2">
                                            <h3 className="text-white font-semibold line-clamp-1 text-sm">{loc.locationName}</h3>
                                            <div className="text-xs text-neutral-500 mt-0.5 flex gap-3">
                                                <span>📅 {new Date(loc.recentObservationDate).toLocaleDateString(language)}</span>
                                                <span>👁 {loc.totalHowMany} {t.seen}</span>
                                            </div>
                                            {/* Source attribution badges */}
                                            {loc.sources && loc.sources.length > 0 && (
                                                <div className="flex gap-1 mt-1.5">
                                                    {loc.sources.includes('ebird') && (
                                                        <span style={{
                                                            fontSize: 10, padding: '1px 6px',
                                                            borderRadius: 20, fontWeight: 600,
                                                            background: '#1d4ed833', color: '#60a5fa',
                                                            border: '1px solid #3b82f640'
                                                        }}>🦅 eBird</span>
                                                    )}
                                                    {loc.sources.includes('inat') && (
                                                        <span style={{
                                                            fontSize: 10, padding: '1px 6px',
                                                            borderRadius: 20, fontWeight: 600,
                                                            background: '#05603433', color: '#34d399',
                                                            border: '1px solid #10b98140'
                                                        }}>🌿 iNat</span>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                            <span className="text-indigo-400 font-bold text-xs whitespace-nowrap bg-indigo-900/40 px-2 py-0.5 rounded-full">
                                                {Math.round(loc.distanceKm)}km
                                            </span>
                                            <span className="text-emerald-500 text-[10px] group-hover:translate-x-0.5 transition-transform">{t.flyToMap}</span>
                                        </div>
                                    </div>

                                    {/* Species strip */}
                                    {loc.matchingSpecies && loc.matchingSpecies.length > 0 && (
                                        <div className="flex gap-2 px-3 pb-3 overflow-x-auto scrollbar-hide">
                                            {loc.matchingSpecies.map(sp => (
                                                <div 
                                                    key={sp.speciesId} 
                                                    className="flex-shrink-0 flex flex-col items-center gap-1 w-14"
                                                    title={sp.scientificName}
                                                >
                                                    {sp.photoUrl ? (
                                                        <div 
                                                            className="w-12 h-12 rounded-full overflow-hidden border-2"
                                                            style={{ borderColor: sp.themeColor || '#10b981' }}
                                                        >
                                                            <img 
                                                                src={sp.photoUrl} 
                                                                alt={sp.commonName} 
                                                                className="w-full h-full object-cover"
                                                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div 
                                                            className="w-12 h-12 rounded-full bg-neutral-800 border-2 flex items-center justify-center text-neutral-500 text-lg"
                                                            style={{ borderColor: sp.themeColor || '#10b981' }}
                                                        >
                                                            🦜
                                                        </div>
                                                    )}
                                                    <span className="text-[9px] text-neutral-400 text-center leading-tight line-clamp-2">
                                                        {sp.commonName}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {/* Selected indicator */}
                                    {isSelected && (
                                        <div className="flex items-center gap-1 px-3 py-1.5 bg-indigo-500/10 border-t border-indigo-500/20">
                                            <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-pulse" />
                                            <span className="text-indigo-400 text-[10px] font-medium">{t.highlightedOnMap}</span>
                                        </div>
                                    )}
                                </div>
                                );
                            })}
                        </div>
                    </>
                )}

                {/* ─── QUESTS TAB ─── */}
                {activeTab === "quests" && (
                    <>
                        {!user && (
                            <div className="text-center p-8 bg-neutral-900/50 rounded-xl border border-neutral-800">
                                <div className="text-4xl mb-3">🔐</div>
                                <p className="text-neutral-300 text-sm font-medium">{t.signInQuestsTitle}</p>
                                <p className="text-neutral-600 text-xs mt-1">{t.signInQuestsHint}</p>
                            </div>
                        )}

                        {user && savedTargets.length === 0 && (
                            <div className="text-center p-8 bg-neutral-900/30 rounded-xl border border-dashed border-neutral-800">
                                <div className="text-4xl mb-2">🗺️</div>
                                <p className="text-neutral-400 text-sm font-medium">{t.noQuestsTitle}</p>
                                <p className="text-neutral-600 text-xs mt-1">{t.noQuestsHint}</p>
                                <button
                                    onClick={() => setActiveTab("search")}
                                    className="mt-4 text-xs text-indigo-400 hover:text-indigo-300 underline"
                                >
                                    {t.goToSearch}
                                </button>
                            </div>
                        )}

                        {user && savedTargets.length > 0 && (
                            <div className="flex flex-col gap-2">
                                <p className="text-xs text-neutral-600 font-medium uppercase tracking-wider">
                                    {savedQuestsLabel}
                                </p>
                                {savedTargets.map(target => (
                                    <div 
                                        key={target.id}
                                        className="bg-neutral-900 border border-neutral-800 rounded-xl p-3 flex items-center gap-3 group hover:border-neutral-700 transition"
                                    >
                                        <div className="w-9 h-9 rounded-lg bg-indigo-900/40 border border-indigo-500/30 flex items-center justify-center text-lg flex-shrink-0">
                                            🏆
                                        </div>
                                        <div 
                                            className="flex-1 min-w-0 cursor-pointer"
                                            onClick={() => handleLoadTarget(target)}
                                        >
                                            <p className="text-white text-sm font-semibold truncate">{target.label}</p>
                                            <p className="text-neutral-500 text-xs truncate">
                                                {target.searchTerm || target.speciesName || t.unknownTarget}
                                            </p>
                                        </div>
                                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition flex-shrink-0">
                                            <button 
                                                onClick={() => handleLoadTarget(target)}
                                                title={t.runQuest}
                                                className="p-1.5 rounded-lg bg-indigo-500/10 hover:bg-indigo-500/30 text-indigo-400 text-xs transition"
                                            >
                                                ▶
                                            </button>
                                            <button 
                                                onClick={() => idToken && deleteTarget(idToken, target.id)}
                                                title={t.delete}
                                                className="p-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/30 text-red-400 text-xs transition"
                                            >
                                                🗑
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
